import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/* ─── palette — pulled from logo gradient ──────────────── */
const C = {
  blue:     '#6078EA',
  teal:     '#17EAD9',
  cyan:     '#1BCEDF',
  blueRgb:  '96,120,234',
  tealRgb:  '23,234,217',
  grad:     'linear-gradient(135deg, #6078EA 0%, #17EAD9 100%)',
  gradSoft: 'linear-gradient(135deg, rgba(96,120,234,0.18) 0%, rgba(23,234,217,0.08) 100%)',
};

/* ─── scroll-triggered fade-in ─────────────────────────── */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}
function FadeIn({ children, delay = 0, style = {} }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ─── fake blurred dashboard mockup ────────────────────── */
function DashMockup({ type = 'inbox' }) {
  const isInbox = type === 'inbox';
  return (
    <div style={{
      width: '100%', borderRadius: 16, overflow: 'hidden',
      border: `1px solid rgba(${C.blueRgb},0.2)`,
      background: 'rgba(14,18,30,0.9)',
      boxShadow: `0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(${C.blueRgb},0.15)`,
      position: 'relative',
    }}>
      {/* Toolbar strip */}
      <div style={{ height: 36, background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px' }}>
        {['#ff5f57','#ffbd2e','#28c840'].map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.7 }} />)}
        <div style={{ flex: 1, height: 18, background: 'rgba(255,255,255,0.07)', borderRadius: 4, marginLeft: 8 }} />
      </div>

      {isInbox ? (
        /* Inbox mockup */
        <div style={{ display: 'flex', height: 260 }}>
          {/* Sidebar */}
          <div style={{ width: 200, borderRight: '1px solid rgba(255,255,255,0.06)', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { name: 'Money A.', msg: 'Interested in Grand…', dot: C.blue },
              { name: 'Jordan T.', msg: 'What\'s the trade-in…', dot: C.teal },
              { name: 'Sarah M.', msg: 'Can I book Saturday?', dot: null },
              { name: 'Dave K.', msg: 'Looking for a truck', dot: null },
              { name: 'Priya S.', msg: 'My VIN is 1C4RDJ…', dot: C.cyan },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, background: i === 0 ? `rgba(${C.blueRgb},0.1)` : 'transparent' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: `rgba(${C.blueRgb},0.25)`, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ height: 8, width: 70, background: 'rgba(255,255,255,0.25)', borderRadius: 4, marginBottom: 4 }} />
                  <div style={{ height: 6, width: 90, background: 'rgba(255,255,255,0.1)', borderRadius: 3 }} />
                </div>
                {r.dot && <div style={{ width: 7, height: 7, borderRadius: '50%', background: r.dot, marginLeft: 'auto', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
          {/* Chat area */}
          <div style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { from: true,  w: 160 },
              { from: false, w: 200 },
              { from: true,  w: 130 },
              { from: false, w: 220, ai: true },
              { from: true,  w: 100 },
            ].map((b, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: b.from ? 'flex-start' : 'flex-end' }}>
                <div style={{
                  height: 28, width: b.w,
                  borderRadius: b.from ? '12px 12px 12px 4px' : '12px 12px 4px 12px',
                  background: b.from
                    ? 'rgba(255,255,255,0.1)'
                    : b.ai
                      ? `rgba(${C.blueRgb},0.25)`
                      : `rgba(${C.blueRgb},0.15)`,
                  border: b.ai ? `1px solid rgba(${C.blueRgb},0.4)` : 'none',
                }} />
              </div>
            ))}
          </div>
          {/* Right panel */}
          <div style={{ width: 140, borderLeft: '1px solid rgba(255,255,255,0.06)', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['CUSTOMER','VEHICLE DESIRED','TRADE-IN'].map((label, i) => (
              <div key={i}>
                <div style={{ height: 5, width: 60, background: `rgba(${C.blueRgb},0.5)`, borderRadius: 3, marginBottom: 6 }} />
                {[55,70,45].map((w, j) => (
                  <div key={j} style={{ height: 5, width: w, background: 'rgba(255,255,255,0.1)', borderRadius: 3, marginBottom: 4 }} />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Campaigns mockup */
        <div style={{ padding: 16, height: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
            {[
              { label: 'Sent', val: '2,847', color: C.blue },
              { label: 'Opened', val: '68%', color: C.teal },
              { label: 'Replied', val: '34%', color: C.cyan },
              { label: 'Booked', val: '127', color: C.blue },
            ].map((s) => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ fontSize: 11, color: s.color, fontWeight: 700, marginBottom: 4 }}>{s.val}</div>
                <div style={{ height: 5, width: 36, background: 'rgba(255,255,255,0.15)', borderRadius: 3 }} />
              </div>
            ))}
          </div>
          {/* Campaign rows */}
          {[
            { name: 'Spring Sale Blast', sent: 840, pct: 72, color: C.blue },
            { name: 'Trade-In Conquest', sent: 1200, pct: 58, color: C.teal },
            { name: 'Expiring Lease Follow-up', sent: 450, pct: 81, color: C.cyan },
          ].map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
              <div style={{ width: 130, height: 7, background: 'rgba(255,255,255,0.2)', borderRadius: 4 }} />
              <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${c.pct}%`, height: '100%', background: `linear-gradient(90deg, ${c.color}, rgba(255,255,255,0.3))`, borderRadius: 3 }} />
              </div>
              <div style={{ width: 30, height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 3 }} />
            </div>
          ))}
        </div>
      )}

      {/* Frosted blur overlay at bottom */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
        background: 'linear-gradient(to bottom, transparent, rgba(10,13,20,0.95))',
        pointerEvents: 'none',
      }} />
    </div>
  );
}

/* ─── feature cards ─────────────────────────────────────── */
const FEATURES = [
  { icon: '🤖', title: 'AI Sales Agent',        desc: 'Your AI rep texts every inbound lead instantly — answers questions, qualifies buyers, and books appointments around the clock.' },
  { icon: '🎯', title: 'Lead Intelligence',      desc: 'Every conversation is scored and staged automatically. Hot leads surface first so your team focuses on buyers ready to move.' },
  { icon: '🚗', title: 'Trade-In Appraisals',    desc: 'Customers send their VIN by text. LotMate decodes it, pulls market data, and queues a suggested value for your appraiser.' },
  { icon: '📦', title: 'Inventory Matching',     desc: 'When a customer describes what they want, the AI searches your live lot and sends matching vehicles — photos included.' },
  { icon: '📅', title: 'Appointment Booking',    desc: 'Customers book directly in the conversation. Date, time, and vehicle of interest captured automatically.' },
  { icon: '📊', title: 'Dealer Dashboard',       desc: 'One place to see every conversation, lead status, appraisal, and campaign. Nothing falls through the cracks.' },
];

const HOW_IT_WORKS = [
  { num: '01', title: 'Customer Texts In',          desc: 'A lead comes in from your website, a campaign, or a referral — via SMS. LotMate picks it up instantly.' },
  { num: '02', title: 'AI Handles the Conversation', desc: 'Your AI agent qualifies the buyer, matches inventory, collects trade-in info, and books the appointment — all over text.' },
  { num: '03', title: 'Your Team Closes the Deal',   desc: 'Your staff steps in with full context — lead score, vehicle interest, trade details, and appointment time already set.' },
];

/* ─── hamburger icon ─────────────────────────────────────── */
function HamburgerIcon({ open }) {
  const bar = (top, rotate, opacity = 1) => ({
    display: 'block', height: 2, width: 22, borderRadius: 2,
    background: '#fff', transition: 'transform 0.22s, opacity 0.22s',
    transformOrigin: 'center',
    transform: rotate, opacity,
    position: 'absolute', left: 0, top,
  });
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: 22, height: 16 }}>
      <span style={bar(0,  open ? 'translateY(7px) rotate(45deg)' : 'none')} />
      <span style={bar(7,  'none', open ? 0 : 1)} />
      <span style={bar(14, open ? 'translateY(-7px) rotate(-45deg)' : 'none')} />
    </span>
  );
}

/* ─── page ──────────────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMobileMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const NAV_LINKS = [
    ['#features', 'Features'],
    ['#how-it-works', 'How it works'],
    ['mailto:info@lotmate.io', 'Contact'],
  ];

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: '#080b12', color: '#f0f2f8', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* ── Navbar ───────────────────────────────────────── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(18px)',
        background: 'rgba(8,11,18,0.88)',
      }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 20px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <img src="/logo.svg" alt="LotMate" style={{ height: 48, width: 'auto' }} />
            <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px', background: C.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', whiteSpace: 'nowrap' }}>LotMate</span>
          </div>

          {/* Desktop: Links + CTA — hidden below 768px via inline media-query workaround */}
          <div className="landing-nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {NAV_LINKS.map(([href, label]) => (
              <a key={label} href={href}
                style={{ color: 'rgba(240,242,248,0.55)', fontSize: 14, textDecoration: 'none', padding: '7px 14px', borderRadius: 8, transition: 'color 0.18s', whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(240,242,248,0.55)'}
              >{label}</a>
            ))}
            <button
              onClick={() => navigate('/login')}
              style={{
                marginLeft: 8, background: C.grad,
                color: '#fff', border: 'none', borderRadius: 10,
                padding: '9px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                boxShadow: `0 4px 18px rgba(${C.blueRgb},0.4)`,
                transition: 'opacity 0.18s, transform 0.15s', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >Dealer Login</button>
          </div>

          {/* Mobile: Hamburger button — hidden above 768px */}
          <button
            className="landing-nav-hamburger"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <HamburgerIcon open={mobileMenuOpen} />
          </button>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div
            className="landing-nav-mobile-menu"
            style={{
              background: 'rgba(10,13,22,0.97)',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              padding: '12px 20px 20px',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}
          >
            {NAV_LINKS.map(([href, label]) => (
              <a
                key={label} href={href}
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  color: 'rgba(240,242,248,0.75)', fontSize: 16,
                  textDecoration: 'none', padding: '12px 8px',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  display: 'block',
                }}
              >{label}</a>
            ))}
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
              style={{
                marginTop: 12, background: C.grad,
                color: '#fff', border: 'none', borderRadius: 10,
                padding: '13px 24px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                width: '100%',
              }}
            >Dealer Login</button>
          </div>
        )}
      </nav>

      {/* ── Hero ─────────────────────────────────────────── */}
      <section style={{ maxWidth: 1160, margin: '0 auto', padding: '80px 28px 60px', position: 'relative' }}>
        {/* bg glow */}
        <div style={{ position: 'absolute', top: 0, left: '30%', width: 700, height: 500, borderRadius: '50%', background: `radial-gradient(ellipse, rgba(${C.blueRgb},0.1) 0%, transparent 65%)`, pointerEvents: 'none', zIndex: 0 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 64, position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
          {/* Left: copy */}
          <div style={{ flex: '1 1 440px', animation: 'heroIn 0.75s ease forwards' }}>
            {/* badge */}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: `rgba(${C.blueRgb},0.12)`, border: `1px solid rgba(${C.blueRgb},0.28)`,
              borderRadius: 999, padding: '5px 16px', fontSize: 11, fontWeight: 700,
              color: C.teal, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 28,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.teal, display: 'inline-block', animation: 'blink 2s infinite' }} />
              AI-Powered Automotive Sales
            </span>

            <h1 style={{
              fontSize: 'clamp(38px, 5.5vw, 66px)', fontWeight: 900,
              lineHeight: 1.08, letterSpacing: '-2.5px', marginBottom: 22,
              background: `linear-gradient(135deg, #ffffff 0%, #b8d4f8 50%, ${C.teal} 100%)`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              Your lot sells<br />while you sleep.
            </h1>

            <p style={{ fontSize: 18, color: 'rgba(240,242,248,0.6)', maxWidth: 480, lineHeight: 1.7, marginBottom: 40 }}>
              LotMate is the AI sales agent built for car dealerships. It texts leads, qualifies buyers, books appointments, and runs trade-in appraisals — automatically.
            </p>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <a href="/audit" style={{
                display: 'inline-block',
                background: C.grad, color: '#fff',
                border: 'none', borderRadius: 12,
                padding: '14px 34px', fontSize: 16, fontWeight: 700, textDecoration: 'none',
                boxShadow: `0 8px 28px rgba(${C.blueRgb},0.45)`,
                transition: 'transform 0.15s, opacity 0.18s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >Get Started Free →</a>
              <a href="mailto:info@lotmate.io" style={{
                display: 'inline-block',
                background: 'rgba(255,255,255,0.06)', color: 'rgba(240,242,248,0.82)',
                border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12,
                padding: '14px 34px', fontSize: 16, fontWeight: 600, textDecoration: 'none',
                transition: 'background 0.18s, transform 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >Get in touch</a>
            </div>
          </div>

          {/* Right: inbox mockup */}
          <div style={{ flex: '1 1 420px', animation: 'heroIn 0.75s ease 150ms both' }}>
            <DashMockup type="inbox" />
          </div>
        </div>
      </section>

      {/* ── Stats bar ────────────────────────────────────── */}
      <FadeIn>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.025)' }}>
          <div style={{ maxWidth: 1160, margin: '0 auto', padding: '30px 28px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 20, textAlign: 'center' }}>
            {[
              { value: '24 / 7', label: 'AI always online' },
              { value: '< 60s',  label: 'First response time' },
              { value: '100%',   label: 'Leads followed up' },
              { value: '3 min',  label: 'VIN to appraisal' },
            ].map((s) => (
              <div key={s.label}>
                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-1px', background: C.grad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{s.value}</div>
                <div style={{ fontSize: 12, color: 'rgba(240,242,248,0.4)', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      {/* ── Features ─────────────────────────────────────── */}
      <section id="features" style={{ maxWidth: 1160, margin: '0 auto', padding: '96px 28px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-1.2px', marginBottom: 14 }}>Everything your store needs</h2>
            <p style={{ color: 'rgba(240,242,248,0.48)', fontSize: 17, maxWidth: 500, margin: '0 auto' }}>One platform. Every touchpoint covered — from first text to signed deal.</p>
          </div>
        </FadeIn>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
          {FEATURES.map((f, i) => (
            <FadeIn key={f.title} delay={i * 70}>
              <div
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: '28px 28px 32px', height: '100%', transition: 'border-color 0.2s, transform 0.2s, background 0.2s', cursor: 'default' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `rgba(${C.blueRgb},0.35)`; e.currentTarget.style.background = `rgba(${C.blueRgb},0.04)`; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: 30, marginBottom: 14 }}>{f.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10, color: '#fff' }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: 'rgba(240,242,248,0.5)', lineHeight: 1.72, margin: 0 }}>{f.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── Product preview (campaigns) ──────────────────── */}
      <section style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '80px 28px', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 64, flexWrap: 'wrap' }}>
          <FadeIn style={{ flex: '1 1 380px' }}>
            <div>
              <span style={{ display: 'inline-block', background: `rgba(${C.tealRgb},0.12)`, border: `1px solid rgba(${C.tealRgb},0.25)`, borderRadius: 999, padding: '4px 14px', fontSize: 11, fontWeight: 700, color: C.teal, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 20 }}>Campaigns</span>
              <h2 style={{ fontSize: 'clamp(26px, 3.5vw, 38px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: 16, lineHeight: 1.15 }}>Send smarter.<br />Track everything.</h2>
              <p style={{ color: 'rgba(240,242,248,0.52)', fontSize: 16, lineHeight: 1.72, marginBottom: 0 }}>
                Launch targeted SMS campaigns to conquest leads, re-engage your database, or follow up on expiring leases. Real-time open, reply, and booking stats — all in one view.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={150} style={{ flex: '1 1 420px' }}>
            <DashMockup type="campaigns" />
          </FadeIn>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────── */}
      <section id="how-it-works" style={{ maxWidth: 1160, margin: '0 auto', padding: '96px 28px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-1.2px', marginBottom: 14 }}>How it works</h2>
            <p style={{ color: 'rgba(240,242,248,0.48)', fontSize: 17, maxWidth: 440, margin: '0 auto' }}>Up and running in minutes. No complicated setup.</p>
          </div>
        </FadeIn>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 48 }}>
          {HOW_IT_WORKS.map((step, i) => (
            <FadeIn key={step.num} delay={i * 120}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: 60, height: 60, borderRadius: '50%', margin: '0 auto 22px',
                  background: `rgba(${C.blueRgb},0.12)`, border: `1px solid rgba(${C.blueRgb},0.3)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 900, color: C.teal, letterSpacing: '1px',
                }}>{step.num}</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: 'rgba(240,242,248,0.48)', lineHeight: 1.75, maxWidth: 280, margin: '0 auto' }}>{step.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section style={{ padding: '40px 28px 96px' }}>
        <FadeIn>
          <div style={{ maxWidth: 900, margin: '0 auto', background: C.gradSoft, border: `1px solid rgba(${C.blueRgb},0.22)`, borderRadius: 28, padding: '72px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -80, right: -80, width: 360, height: 360, borderRadius: '50%', background: `radial-gradient(circle, rgba(${C.tealRgb},0.1) 0%, transparent 65%)`, pointerEvents: 'none' }} />
            <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 40px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: 16 }}>Ready to see it in action?</h2>
            <p style={{ color: 'rgba(240,242,248,0.52)', fontSize: 17, maxWidth: 440, margin: '0 auto 38px', lineHeight: 1.65 }}>
              Reach out and we'll walk you through exactly how LotMate works for your store.
            </p>
            <a href="mailto:info@lotmate.io" style={{
              display: 'inline-block', background: C.grad,
              color: '#fff', borderRadius: 12, padding: '15px 40px',
              fontSize: 16, fontWeight: 700, textDecoration: 'none',
              boxShadow: `0 8px 28px rgba(${C.blueRgb},0.45)`,
              transition: 'transform 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >info@lotmate.io</a>
          </div>
        </FadeIn>
      </section>

      {/* ── Footer ───────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.07)', padding: '28px 28px' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/logo.svg" alt="LotMate" style={{ height: 36, width: 'auto', opacity: 0.65 }} />
            <span style={{ fontSize: 14, color: 'rgba(240,242,248,0.35)', fontWeight: 700 }}>LotMate</span>
          </div>
          <span style={{ fontSize: 13, color: 'rgba(240,242,248,0.28)' }}>© {new Date().getFullYear()} LotMate Inc.</span>
          <a href="mailto:info@lotmate.io"
            style={{ fontSize: 13, color: 'rgba(240,242,248,0.38)', textDecoration: 'none', transition: 'color 0.18s' }}
            onMouseEnter={e => e.currentTarget.style.color = C.teal}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(240,242,248,0.38)'}
          >info@lotmate.io</a>
        </div>
      </footer>

      <style>{`
        @keyframes heroIn  { from { opacity: 0; transform: translateY(32px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes blink   { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
      `}</style>
    </div>
  );
}
